/**
 * TrajectoryChart Component
 *
 * Dual-line Recharts visualization showing SSOT baseline vs Zandi-adjusted projections.
 * Used for rent growth trajectory and property value trajectory.
 *
 * Design: Dashed stone baseline line + solid gold Zandi-adjusted line with themed tooltips.
 */

import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrencyCompact } from '../../utils/formatters';

export interface TrajectoryChartProps {
  title: string;                    // e.g., "Rent Growth Trajectory"
  subtitle?: string;                // e.g., "SSOT baseline vs Zandi momentum-adjusted"
  summaryLine?: string;             // Optional manual override for the delta summary
  baselineData: number[];           // 10 yearly values (years 1-10), SSOT flat growth
  adjustedData: number[];           // 10 yearly values (years 1-10), Zandi-adjusted
  confidenceHighData?: number[];    // Bull case trajectory (years 1-10)
  confidenceLowData?: number[];     // Bear case trajectory (years 1-10)
  formatValue?: (value: number) => string;  // Formatter for tooltips & summary (full: "$450,000")
  formatAxisValue?: (value: number) => string;  // Compact formatter for Y-axis labels ("$450K")
  height?: number;                  // Chart height, default 280
}

export function TrajectoryChart({
  title,
  subtitle,
  summaryLine,
  baselineData,
  adjustedData,
  confidenceHighData,
  confidenceLowData,
  formatValue,
  formatAxisValue,
  height = 280,
}: TrajectoryChartProps) {
  // Handle empty data - return null if no data to display
  if (!baselineData || baselineData.length === 0 || !adjustedData || adjustedData.length === 0) {
    return null;
  }

  const hasConfidenceBand = confidenceHighData && confidenceLowData
    && confidenceHighData.length > 0 && confidenceLowData.length > 0;

  // Transform data into chart format
  const chartData = baselineData.map((baseline, idx) => ({
    year: `Year ${idx + 1}`,
    baseline,
    adjusted: adjustedData[idx],
    ...(hasConfidenceBand ? {
      confidenceRange: [confidenceLowData[idx], confidenceHighData[idx]] as [number, number],
    } : {}),
  }));

  // Default formatter if none provided
  const valueFormatter = formatValue || ((value: number) => String(value));
  const axisFormatter = formatAxisValue || formatCurrencyCompact;

  // Auto-compute delta summary
  const baselineTotal = baselineData.reduce((sum, v) => sum + v, 0);
  const adjustedTotal = adjustedData.reduce((sum, v) => sum + v, 0);
  const delta = adjustedTotal - baselineTotal;
  const deltaPct = baselineTotal !== 0 ? (delta / baselineTotal) * 100 : 0;
  const autoSummary = delta !== 0
    ? `Market-adjusted total is ${valueFormatter(Math.abs(delta))} ${delta > 0 ? 'higher' : 'lower'} over ${baselineData.length} years (${delta > 0 ? '+' : ''}${deltaPct.toFixed(0)}%)`
    : null;

  return (
    <div className="rounded-lg border border-slate-dark bg-charcoal p-6">
      {/* Title */}
      <h3 className="text-gold uppercase tracking-[0.12em] font-semibold text-sm mb-1">
        {title}
      </h3>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-sm text-stone-700 mb-2">
          {subtitle}
        </p>
      )}

      {/* Delta Summary */}
      {(summaryLine || autoSummary) && (
        <p className="text-sm text-ivory/70 leading-relaxed mb-4">
          {summaryLine || autoSummary}
        </p>
      )}

      {/* Recharts ComposedChart */}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
        >
          {/* Grid */}
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#d4d1cc"
            opacity={0.2}
          />

          {/* X Axis */}
          <XAxis
            dataKey="year"
            stroke="#d4d1cc"
            style={{
              fontSize: '11px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
          />

          {/* Y Axis */}
          <YAxis
            stroke="#d4d1cc"
            width={70}
            style={{
              fontSize: '11px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
            tickFormatter={axisFormatter}
          />

          {/* Tooltip */}
          <Tooltip
            contentStyle={{
              backgroundColor: '#f5f4f1',
              border: '1px solid #d4d1cc',
              borderRadius: '8px',
              fontFamily: 'JetBrains Mono, monospace',
            }}
            labelStyle={{ color: '#d4a853', fontWeight: 600 }}
            formatter={(value: number | [number, number], name: string) => {
              if (name === 'Confidence Band' && Array.isArray(value)) {
                return `${valueFormatter(value[0])} – ${valueFormatter(value[1])}`;
              }
              return valueFormatter(value as number);
            }}
          />

          {/* Legend */}
          <Legend
            wrapperStyle={{
              fontSize: '12px',
              fontFamily: 'Inter, sans-serif',
            }}
          />

          {/* Confidence Band - shaded gold region (rendered first, behind lines) */}
          {hasConfidenceBand && (
            <Area
              type="monotone"
              dataKey="confidenceRange"
              fill="#d4a853"
              fillOpacity={0.12}
              stroke="#d4a853"
              strokeOpacity={0.25}
              strokeWidth={1}
              strokeDasharray="3 3"
              name="Confidence Band"
              connectNulls={true}
            />
          )}

          {/* Base Case Line - dashed stone */}
          <Line
            type="monotone"
            dataKey="baseline"
            stroke="#78716c"
            strokeDasharray="5 5"
            strokeWidth={2}
            name="Base Case"
            dot={false}
            connectNulls={true}
          />

          {/* Market-Adjusted Line - solid gold */}
          <Line
            type="monotone"
            dataKey="adjusted"
            stroke="#d4a853"
            strokeWidth={3}
            name="Market-Adjusted"
            dot={{ fill: '#d4a853', r: 4 }}
            activeDot={{ r: 6 }}
            connectNulls={true}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
